import { CpDataTable } from "@components";
import {
  TableSchema,
  ColumnId,
  DataType,
  CellComponent,
  FilterComponent,
  RawColumnSchema,
  RawTableSchema,
  ColumnSchema,
} from "./types";
import { SelectAllHeader } from "./headers/select-all-header.component";
import { SelectCell } from "./cells";

export const columnWidthsPx: Record<string, number> = {
  sm: 110,
  md: 200,
  lg: 300,
};

// Helper functions for right sticky column positioning
export function getRightStickyColumns(
  orderedColumns: ColumnId[],
  schema: TableSchema,
): ColumnId[] {
  return orderedColumns.filter(
    (columnId) => schema[columnId]?.sticky === "right",
  );
}

export function calculateRightStickyPosition(
  columnId: ColumnId,
  orderedColumns: ColumnId[],
  schema: TableSchema,
  columnWidths: Record<string, number | string>,
): number {
  const rightStickyColumns = getRightStickyColumns(orderedColumns, schema);
  const columnIndex = rightStickyColumns.indexOf(columnId);

  if (columnIndex === -1) return 0;

  // Calculate total width of columns to the right of this column
  let rightOffset = 0;
  for (let i = columnIndex + 1; i < rightStickyColumns.length; i++) {
    const rightColumnId = rightStickyColumns[i];
    const width =
      columnWidths[rightColumnId] || schema[rightColumnId]?.width || "md";
    rightOffset += getColumnWidthPx(width) || columnWidthsPx.md;
  }

  return rightOffset;
}

// Used to prevent double borders on right sticky columns
export function isColumnBeforeRightSticky(
  columnId: ColumnId,
  orderedColumns: ColumnId[],
  schema: TableSchema,
): boolean {
  const currentIndex = orderedColumns.indexOf(columnId);
  if (currentIndex === -1 || currentIndex === orderedColumns.length - 1)
    return false;

  // Check if the next column is right sticky
  const nextColumnId = orderedColumns[currentIndex + 1];
  return schema[nextColumnId]?.sticky === "right";
}

// Returns a number representing the width of the column in pixels
export function getColumnWidthPx(
  widthValue?: string | number,
): number | undefined {
  if (!widthValue) return undefined;
  return typeof widthValue === "number"
    ? widthValue
    : columnWidthsPx[widthValue];
}

// Returns a copy of colsToSort sorted by the columnOrder
export function sortColumns(
  schema: TableSchema,
  colsToSort: ColumnId[],
  columnOrder: ColumnId[],
): ColumnId[] {
  return [...colsToSort].sort((a, b) => {
    const aIsLeftSticky =
      schema[a]?.sticky === "left" || schema[a]?.sticky === true;
    const bIsLeftSticky =
      schema[b]?.sticky === "left" || schema[b]?.sticky === true;
    const aIsRightSticky = schema[a]?.sticky === "right";
    const bIsRightSticky = schema[b]?.sticky === "right";

    // Left sticky columns come first
    if (aIsLeftSticky && !bIsLeftSticky) return -1;
    if (!aIsLeftSticky && bIsLeftSticky) return 1;

    // Right sticky columns come last
    if (aIsRightSticky && !bIsRightSticky) return 1;
    if (!aIsRightSticky && bIsRightSticky) return -1;

    let indexA = columnOrder.indexOf(a);
    let indexB = columnOrder.indexOf(b);
    // If index cannot be found then push these columns to the end of the list
    if (indexA === -1) indexA = Infinity;
    if (indexB === -1) indexB = Infinity;

    return indexA - indexB;
  });
}

function getCellComponentFromDataType(
  dataType: DataType,
): CellComponent<any> | null {
  switch (dataType) {
    case "string":
    case "number":
      return CpDataTable.TextCell;
    case "bool":
      return CpDataTable.BoolCell;
    case "date_iso":
    case "date_unix_ms":
      return CpDataTable.DateCell;
    case "array":
      return CpDataTable.ListCell;
    default:
      return null;
  }
}

function getFilterComponentFromDataType(
  dataType: DataType,
): FilterComponent<any> {
  switch (dataType) {
    case "bool":
      return CpDataTable.BoolFilter;
    case "date_iso":
    case "date_unix_ms":
      return CpDataTable.DateFilter;
    default:
      return CpDataTable.InputFilter;
  }
}

function getFieldId(
  columnSchema: RawColumnSchema,
  type: "filter" | "sort",
): string {
  const typeObj = columnSchema[type];
  if (typeof typeObj === "object" && typeObj.fieldId) {
    return typeObj.fieldId;
  } else if (columnSchema.fieldId) {
    return columnSchema.fieldId;
  }
  throw new Error(`${columnSchema.id} is missing a fieldId for ${type}`);
}

function formatColumnSchema(
  columnId: ColumnId,
  columnSchema: RawColumnSchema,
): ColumnSchema {
  const derivedCell = getCellComponentFromDataType(columnSchema.dataType);

  return {
    ...columnSchema,
    id: columnId,
    cell: {
      component:
        derivedCell || columnSchema?.cell?.component || CpDataTable.TextCell,
      ...columnSchema.cell,
    },
    filter: columnSchema.filter
      ? {
          component: getFilterComponentFromDataType(columnSchema.dataType),
          fieldId: getFieldId(columnSchema, "filter"),
          ...(typeof columnSchema.filter === "object" && columnSchema.filter),
        }
      : false,
    sort: columnSchema.sort
      ? {
          fieldId: getFieldId(columnSchema, "sort"),
          ...(typeof columnSchema.sort === "object" && columnSchema.sort),
        }
      : false,
  };
}

// Takes a schema defined by the consumer and returns a fully formed schema with default values and other useful info
export function formatSchema(schema: RawTableSchema) {
  const newSchema: TableSchema = {
    // Add a default select column if one is not provided.
    // This will only render if the selection prop has been passed.
    select: formatColumnSchema("select", {
      sticky: "left",
      header: {
        component: SelectAllHeader,
      },
      cell: {
        component: SelectCell,
      },
      width: 40,
      resizable: false,
      showVerticalBorder: false,
      ...(schema.select || {}),
      dataType: "component",
    }),
  };

  for (const columnId in schema) {
    if (columnId === "select") continue;
    newSchema[columnId] = formatColumnSchema(columnId, schema[columnId]);
  }
  return newSchema;
}
